import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { TicketFormSchema, UploadResultsSchema } from "@msuite/utils/schemas";
import { useState } from "react";
import { client } from "@/hono";

/** Props Interface */
interface UseProjectTicketsControllerProps {
	projectId: string | null;
}

export function useProjectTicketsController({ projectId }: UseProjectTicketsControllerProps) {
	/** Hooks */

	/** State */
	const [newTickets, setNewTickets] = useState<(TicketFormSchema & UploadResultsSchema)[]>([]);

	/** Query */
	const { data: currentTickets, refetch } = useQuery({
		queryKey: ["tickets", projectId],
		queryFn: async () => {
			if (!projectId) throw new Error("Project ID needed.");
			const response = await client.projects[":projectId"].tickets.$get({
				param: {
					projectId,
				},
				query: {
					state: ["active", "inactive", "archived"],
				},
			});
			const data = await responseHandler(response);
			return data.tickets;
		},
		enabled: !!projectId,
	});

	/** Functions */
	async function handleOnSubmit(projectId: string) {
		const promises = [];
		for (const newTicket of newTickets) {
			promises.push(
				client.tickets.index
					.$post({
						json: {
							...newTicket,
							projectId,
						},
					})
					// A queued ticket the route refuses has to say so: without this the project saves
					// and the tickets it promised are simply not there.
					.then((response) => responseHandler(response)),
			);
		}
		await Promise.all(promises);
	}

	async function handleOnReset() {
		setNewTickets([]);
	}

	function addNewTicket(ticket: TicketFormSchema & UploadResultsSchema) {
		setNewTickets((prev) => [...prev, ticket]);
	}

	const controller = {
		currentTickets: currentTickets || [],
		newTickets,
		addNewTicket,
		refetch,
	};

	/** Render */
	return {
		controller,
		handleOnSubmit,
		handleOnReset,
	};
}

export type ProjectTicketsController = ReturnType<typeof useProjectTicketsController>["controller"];
